import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    App,
    Button,
    Card,
    Col,
    Empty,
    Image as AntImage,
    Modal,
    Row,
    Space,
    Spin,
    Typography,
    Upload,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { listSplashImages, uploadSplashImage, deleteSplashImage } from '../../services/carousel';
import { resolveMedia } from '../../utils/media';
import { SPLASH_ROLES_OPTIONS } from '../../utils/constants';
import { Select } from 'antd';
import { Form } from 'antd';

// Helper para obtener el label de un rol
const getRoleLabel = (roleValue) => {
    const role = SPLASH_ROLES_OPTIONS.find(r => r.value === roleValue);
    return role ? role.label : roleValue;
};

const { Dragger } = Upload;
const { Title, Paragraph, Text } = Typography;

const MAX_FILE_SIZE = 2048 * 1024; // 300 KB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];
const EXPECTED_WIDTH = 1080;
const EXPECTED_HEIGHT = 1920;
const RATIO = 9 / 16;
const RATIO_TOLERANCE = 0.01;

const CarouselPage = () => {
    const { message, modal } = App.useApp();

    const [images, setImages] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [selectedRole, setSelectedRole] = useState(undefined);
    const [uploading, setUploading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    /**
     * Carga todas las imágenes existentes al montar la pantalla.
     */
    const fetchImages = useCallback(async () => {
        setLoadingList(true);
        try {
            const data = await listSplashImages();
            setImages(Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
        } catch (error) {
            console.error(error);
            message.error('No se pudieron cargar las imágenes.');
        } finally {
            setLoadingList(false);
        }
    }, [message]);

    useEffect(() => {
        fetchImages();
    }, [fetchImages]);

    /**
     * Agrupa las imágenes por rol.
     */
    const imagesByRole = useMemo(() => {
        const grouped = {};
        images.forEach((image) => {
            const role = image.role || 'TODOS';
            if (!grouped[role]) {
                grouped[role] = [];
            }
            grouped[role].push(image);
        });
        
        // Retornar en el orden definido en SPLASH_ROLES_OPTIONS, más cualquier rol adicional
        const orderedRoles = [];
        SPLASH_ROLES_OPTIONS.forEach(({ value }) => {
            if (grouped[value]) {
                orderedRoles.push({ role: value, images: grouped[value] });
            }
        });
        
        // Agregar roles que no están en SPLASH_ROLES_OPTIONS (como TODOS)
        Object.keys(grouped).forEach((role) => {
            if (!SPLASH_ROLES_OPTIONS.find(r => r.value === role)) {
                orderedRoles.push({ role, images: grouped[role] });
            }
        });
        
        return orderedRoles;
    }, [images]);

    /**
     * Validaciones básicas del archivo (tipo y peso).
     */
    const validateFileBasics = useCallback((file) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            message.error('Solo se permiten imágenes JPG o PNG.');
            return false;
        }

        if (file.size > MAX_FILE_SIZE) {
            message.error('El archivo excede el tamaño máximo de 300 KB.');
            return false;
        }

        return true;
    }, [message]);

    /**
     * Verifica dimensiones y proporción antes de subir.
     */
    const validateDimensions = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    const ratio = img.width / img.height;
                    const matchesExactSize = img.width === EXPECTED_WIDTH && img.height === EXPECTED_HEIGHT;
                    const matchesRatio = Math.abs(ratio - RATIO) <= RATIO_TOLERANCE;

                    if (matchesExactSize && matchesRatio) {
                        resolve(true);
                        return;
                    }

                    reject(new Error('La imagen debe ser de 1080 × 1920 píxeles (proporción 9:16).'));
                };
                img.onerror = () => reject(new Error('No se pudo validar la imagen.'));
                img.src = event.target.result;
            };
            reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
            reader.readAsDataURL(file);
        });

    /**
     * Maneja la subida manual del archivo una vez que supera las validaciones.
     */
    const handleUpload = useCallback(async (file, role) => {
        setUploading(true);
        try {
            const newImage = await uploadSplashImage(file, role);
            // Agregar la nueva imagen al array plano (el backend debería devolver la imagen con role)
            setImages((prev) => [...prev, { ...newImage, role: newImage.role || role }]);
            message.success('Imagen subida correctamente.');
        } catch (error) {
            const errorMessage = error?.__normalized?.message ?? 'No se pudo subir la imagen.';
            message.error(errorMessage);
            error.__skipToast = true;
            throw error;
        } finally {
            setUploading(false);
        }
    }, [message]);

    /**
     * Orquesta todas las validaciones antes de lanzar la carga.
     * La función retorna Upload.LIST_IGNORE para evitar que AntD maneje la subida automáticamente.
     */
    const handleBeforeUpload = useCallback(
        async (file, selectedRoleValue) => {

            if (!selectedRoleValue) {
                message.error('Debes seleccionar un rol antes de subir la imagen.');
                return Upload.LIST_IGNORE;
            }

            if (!validateFileBasics(file)) {
                return Upload.LIST_IGNORE;
            }

            try {
                await validateDimensions(file);
                await handleUpload(file, selectedRoleValue);
            } catch (error) {
                if (!error?.__skipToast && error?.message) {
                    message.error(error.message);
                }
            }

            return Upload.LIST_IGNORE;
        },
        [handleUpload, message, validateFileBasics]
    );

    /**
     * Confirma y elimina una imagen seleccionada.
     */
    const handleDelete = useCallback(async (id) => {
        setDeletingId(id);
        try {
            await deleteSplashImage(id);
            // Eliminar la imagen del array plano
            setImages((prev) => prev.filter((img) => img.id !== id));
            message.success('Imagen eliminada correctamente.');
        } catch (error) {
            const errorMessage = error?.__normalized?.message ?? 'No se pudo eliminar la imagen.';
            message.error(errorMessage);
            // throw error;
        } finally {
            setDeletingId(null);
        }
    }, [message]);

    /**
     * Abre el modal de confirmación antes de eliminar físicamente la imagen.
     */
    const confirmDelete = useCallback((image) => {
        modal.confirm({
            title: 'Confirmar eliminación',
            content: '¿Desea eliminar esta imagen? Esta acción no se puede deshacer.',
            okText: 'Eliminar',
            cancelText: 'Cancelar',
            okButtonProps: { danger: true },
            onOk: () => handleDelete(image.id),
        });
    }, [handleDelete, modal]);

    /**
     * Configuración del componente Upload.Dragger para centralizar props.
     */
    const draggerProps = useMemo(
        () => ({
            multiple: false,
            showUploadList: false,
            disabled: uploading,
            accept: '.jpg,.jpeg,.png',
            beforeUpload: handleBeforeUpload,
        }),
        [handleBeforeUpload, uploading]
    );

    const openPreview = (image) => {
        setPreviewImage(image);
        setIsPreviewOpen(true);
    };

    const closePreview = () => {
        setIsPreviewOpen(false);
        setPreviewImage(null);
    };

    const galleryContent = () => {
        if (loadingList) {
            return (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <Spin />
            </div>
            );
        }

        if (!images.length) {
            return <Empty description="Aún no hay imágenes cargadas." />;
        }

        if (imagesByRole.length === 0) {
            return <Empty description="Aún no hay imágenes cargadas." />;
        }

        return (
            <Space direction="vertical" size={32} style={{ width: '100%' }}>
            {imagesByRole.map((group) => (
                <div key={group.role}>
                {/* TÍTULO DE ROL */}
                <Title level={4} style={{ marginBottom: 16 }}>
                    {getRoleLabel(group.role)}
                </Title>

                {/* IMÁGENES DEL ROL */}
                {group.images.length === 0 ? (
                    <Empty description="Sin imágenes para este rol" />
                ) : (
                    <Row gutter={[16, 16]}>
                    {group.images.map((image) => (
                        <Col xs={24} sm={12} md={8} lg={6} key={image.id}>
                        <Card
                            hoverable
                            cover={
                            <AntImage
                                alt="Splash"
                                src={resolveMedia(image.url)}
                                preview={false}
                                style={{
                                height: 240,
                                objectFit: 'cover',
                                cursor: 'pointer',
                                }}
                                onClick={() => openPreview(image)}
                            />
                            }
                            actions={[
                            <Button
                                key="delete"
                                type="text"
                                danger
                                block
                                loading={deletingId === image.id}
                                onClick={() => confirmDelete(image)}
                            >
                                Eliminar imagen
                            </Button>,
                            ]}
                        />
                        </Col>
                    ))}
                    </Row>
                )}
                </div>
            ))}
            </Space>
        );
    };


    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>
                        Carrusel
                    </Title>
                    <Text type="secondary">Administra las imágenes mostradas en la pantalla de bienvenida.</Text>
                </div>

                <Card>
                    <Title level={4} style={{ marginBottom: 12 }}>
                        Recomendaciones para las imágenes:
                    </Title>
                    <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                        <li>
                            <Paragraph style={{ marginBottom: 4 }}>Tamaño estándar: 1080 × 1920 píxeles.</Paragraph>
                        </li>
                        <li>
                            <Paragraph style={{ marginBottom: 4 }}>
                                Proporción: 9:16, ideal para orientación vertical de las pantallas de los smartphones.
                            </Paragraph>
                        </li>
                        <li>
                            <Paragraph style={{ marginBottom: 4 }}>Formatos permitidos: .jpg o .png.</Paragraph>
                        </li>
                        <li>
                            <Paragraph style={{ marginBottom: 0 }}>Peso máximo: 300 kb.</Paragraph>
                        </li>
                    </ul>
                </Card>

                <Card>
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col xs={24} md={12}>
                            <Form.Item label='Rol'>
                                <Select
                                    title='Rol'
                                    placeholder="Selecciona un rol"
                                    options={SPLASH_ROLES_OPTIONS}
                                    showSearch
                                    optionFilterProp="label"
                                    filterOption={(input, option) =>
                                        option?.label?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    value={selectedRole}
                                    onChange={(value) => {
                                        setSelectedRole(value);
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Dragger {...draggerProps}
                        beforeUpload={(file) => handleBeforeUpload(file, selectedRole)}>
                        <Space direction="vertical" size={16}>
                            <InboxOutlined style={{ fontSize: 48, color: '#BFBFBF' }} />
                            <div style={{ textAlign: 'center' }}>
                                <Paragraph style={{ marginBottom: 8 }}>
                                    Arrastra una imagen aquí o usa el botón para seleccionarla.
                                </Paragraph>
                                <Button type="primary" loading={uploading}>
                                    Subir imagen
                                </Button>
                                <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                                    JPG o PNG, 1080×1920 px, máximo 300 KB.
                                </Paragraph>
                            </div>
                        </Space>
                    </Dragger>
                </Card>



                <Card title="Galería">
                    {galleryContent()}
                </Card>
            </Space>

            <Modal
                open={isPreviewOpen}
                title="Vista previa"
                onCancel={closePreview}
                footer={null}
                width={420}
                bodyStyle={{ textAlign: 'center' }}
            >
                {previewImage && (
                    <AntImage
                        alt="Vista previa splash"
                        src={resolveMedia(previewImage.url)}
                        preview={false}
                        style={{ maxHeight: '70vh', objectFit: 'contain' }}
                    />
                )}
            </Modal>
        </div>
    );
};

export default CarouselPage;


